import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';

import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { is1PMacro } from '@confluence/fabric-extension-lib/entry-points/macroClassificationUtils.ts';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';

const i18n = defineMessages({
	fallbackFlagTitle: {
		id: 'full-page-editor.unsupported-macro.fallback-flag.title',
		defaultMessage: 'This element may require you to publish',
		description: 'The title of the fallback flag when an unsupported macro is added',
	},
	fallbackFlagDescription: {
		id: 'full-page-editor.unsupported-macro.fallback-flag.description',
		defaultMessage:
			'To fully display and use this element, publish your page via the <menuIcon></menuIcon> menu on the toolbar.',
		description: 'The body of the fallback flag when an unsupported macro is added',
	},
	fallbackFlagLivePageDescription: {
		id: 'full-page-editor.unsupported-macro.fallback-flag.live-page.description',
		defaultMessage:
			'The results of this element aren’t visible when editing a live doc. Convert this doc to a page and publish it via the <menuIcon></menuIcon> menu to display it fully.',
		description: 'The body of the fallback flag when an unsupported macro is added',
	},
	fallbackFlagLearnMoreAction: {
		id: 'full-page-editor.unsupported-macro.fallback-flag.action',
		defaultMessage: 'Learn more about this EAP',
		description: 'The link to learn more on the fallback flag when an unsupported macro is added',
	},
});

export const useMacroFallbackFlag = (flags: FlagsStateContainer, isLivePage: Boolean) => {
	const intl = useIntl();

	const macroFallbackUiAllowlist = useMemo(() => {
		const allowlist = StatsigConfigurations.getStringList(
			'confluence_frontend_macro_fallback_ui_allowlist',
			[],
		);

		return allowlist.map((m) => m.trim().toLowerCase());
	}, []);

	const showMacroFallbackFlag = useCallback(
		async ({
			macro,
			isBodiedExtension,
		}: {
			macro: string | undefined;
			isBodiedExtension: boolean;
		}) => {
			if (
				!macroFallbackUiAllowlist ||
				!macro ||
				!isLivePage ||
				macroFallbackUiAllowlist[0] === 'allow_all'
			) {
				return;
			}

			const macroKey = macro.toLowerCase();

			const is2PNonBodied = !is1PMacro(macroKey) && !isBodiedExtension;
			if (is2PNonBodied || isBodiedExtension) {
				return;
			}

			// If macro is in the allowlist don't show the warning
			if (macroFallbackUiAllowlist.includes(macroKey)) {
				return;
			}

			await flags.showFlag({
				type: 'warning',
				close: 'auto',
				title: intl.formatMessage(i18n.fallbackFlagTitle),
				description: intl.formatMessage(
					isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in')
						? i18n.fallbackFlagLivePageDescription
						: i18n.fallbackFlagDescription,
					{
						menuIcon: () => <MoreIcon label="Icon for publish menu" />,
					},
				),
				messageType: 'transactional',
				messageId: 'editor-macro-fallback-flag',
				// TODO: Change this to the correct link when available
				// actions: [
				//   {
				//     content: intl.formatMessage(i18n.fallbackFlagLearnMoreAction),
				//     href: "#",
				//   },
				// ],
			});
		},
		[flags, macroFallbackUiAllowlist, intl, isLivePage],
	);

	return { showMacroFallbackFlag };
};
